// <tri-spotify-player> — full-track playback for report rows via the
// Spotify Web Playback SDK. One inert element per page owns the player;
// play buttons are plain markup anywhere (summary tables, the
// opportunities overlay), wired by a delegated document listener so
// buttons inside <dialog>s work too:
//
//   <tri-spotify-player></tri-spotify-player>
//   …
//   <button data-spotify-play="4uLU6hMCjMI75M1A2tKUQC" aria-label="Play …">
//     <svg data-icon="play">…</svg>
//     <svg data-icon="pause" hidden>…</svg>
//   </button>
//
// Everything is lazy — nothing loads until the first click, which also
// satisfies browser autoplay-gesture rules:
//   1. fetch /spotify/token. 401 → bounce to /spotify/login (OAuth code
//      flow, SpotifySessionsController) with return_to back to this page.
//   2. inject sdk.scdn.co/spotify-player.js (onSpotifyWebPlaybackSDKReady
//      is set FIRST — the SDK calls it on load), create the "Tritone"
//      Connect device, await `ready` for the device id.
//   3. PUT /v1/me/player/play?device_id=… with the track URI. Clicking the
//      playing row's button toggles pause; another row switches track.
//
// Spotify Premium is an SDK requirement — account_error surfaces once as
// an alert. Button icons swap here (the hidden attribute) driven by
// player_state_changed, so pauses from another device stay in sync.

declare global {
	interface Window {
		onSpotifyWebPlaybackSDKReady: () => void;
		// biome-ignore lint/suspicious/noExplicitAny: SDK ships no types
		Spotify: any;
	}
}

class TriSpotifyPlayer extends HTMLElement {
	// biome-ignore lint/suspicious/noExplicitAny: SDK ships no types
	#player: any = null;
	#deviceId: string | null = null;
	#currentTrack: string | null = null;
	#paused = true;
	#connecting: Promise<void> | null = null;
	#warnedPremium = false;

	async play(trackId: string) {
		if (!trackId) return;

		// Same row while the player exists → plain pause/resume.
		if (trackId === this.#currentTrack && this.#player) {
			this.#player.togglePlay();
			return;
		}

		await this.#ensurePlayer();
		this.#player.activateElement?.(); // mobile autoplay unlock

		const token = await this.#token();
		const response = await fetch(
			`https://api.spotify.com/v1/me/player/play?device_id=${this.#deviceId}`,
			{
				method: "PUT",
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ uris: [`spotify:track:${trackId}`] }),
			},
		);
		if (response.ok) this.#currentTrack = trackId;
	}

	disconnectedCallback() {
		this.#player?.disconnect();
	}

	async #token(): Promise<string> {
		const response = await fetch("/spotify/token", {
			headers: { Accept: "application/json" },
		});
		if (response.status === 401) {
			const back = encodeURIComponent(location.pathname + location.search);
			location.assign(`/spotify/login?return_to=${back}`);
			throw new Error("spotify auth required");
		}
		if (!response.ok)
			throw new Error(`spotify token fetch: ${response.status}`);
		return (await response.json()).access_token;
	}

	#loadSdk(): Promise<void> {
		if (window.Spotify) return Promise.resolve();

		return new Promise((resolve) => {
			// Must exist BEFORE the script executes — the SDK calls it on load.
			window.onSpotifyWebPlaybackSDKReady = () => resolve();
			const script = document.createElement("script");
			script.src = "https://sdk.scdn.co/spotify-player.js";
			document.head.append(script);
		});
	}

	#ensurePlayer(): Promise<void> {
		this.#connecting ??= (async () => {
			await this.#loadSdk();

			this.#player = new window.Spotify.Player({
				name: "Tritone",
				getOAuthToken: (callback: (token: string) => void) => {
					this.#token().then(callback);
				},
				volume: 0.8,
			});

			// biome-ignore lint/suspicious/noExplicitAny: SDK ships no types
			this.#player.addListener("player_state_changed", (state: any) =>
				this.#reflect(state),
			);
			this.#player.addListener("account_error", () => {
				if (this.#warnedPremium) return;
				this.#warnedPremium = true;
				alert("Spotify playback needs a Premium account.");
			});

			const ready = new Promise<void>((resolve) => {
				this.#player.addListener(
					"ready",
					({ device_id }: { device_id: string }) => {
						this.#deviceId = device_id;
						resolve();
					},
				);
			});

			await this.#player.connect();
			await ready;
		})();

		return this.#connecting;
	}

	// Mirror the actual player state onto every play button on the page —
	// active row shows pause, everything else shows play.
	// biome-ignore lint/suspicious/noExplicitAny: SDK ships no types
	#reflect(state: any) {
		this.#paused = state?.paused ?? true;
		const playingId = state?.track_window?.current_track?.id;
		if (playingId) this.#currentTrack = playingId;

		for (const button of document.querySelectorAll<HTMLElement>(
			"[data-spotify-play]",
		)) {
			const active =
				!this.#paused && button.dataset.spotifyPlay === this.#currentTrack;
			button
				.querySelector("[data-icon=play]")
				?.toggleAttribute("hidden", active);
			button
				.querySelector("[data-icon=pause]")
				?.toggleAttribute("hidden", !active);
			button.toggleAttribute("data-playing", active);
		}
	}
}

if (!customElements.get("tri-spotify-player")) {
	customElements.define("tri-spotify-player", TriSpotifyPlayer);

	// One delegated listener for play buttons anywhere in the page —
	// guarded by the define check so Vite HMR can't stack copies.
	document.addEventListener("click", (event) => {
		const target = event.target;
		if (!(target instanceof Element)) return;

		const button = target.closest<HTMLElement>("[data-spotify-play]");
		if (!button) return;

		event.preventDefault();
		document
			.querySelector<TriSpotifyPlayer>("tri-spotify-player")
			?.play(button.dataset.spotifyPlay ?? "");
	});
}

export {};
